//#region Imports
import { useEffect, useState } from 'react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';

import { GlobalVariables } from '../../../../globalVar';
import customAxios from '../../../../utils/customAxios';
import { getToast } from '../../../../utils/toastConfig';
//#endregion

const HEADERS = {
  headers: {
    Authorization: 'bearer ' + localStorage.getItem('token'),
  },
};

/**
 * Component for displaying a modal to update an existing region's details.
 * @param {Object} props - The component props.
 * @param {number} props.regionId - The ID of the failure to update.
 * @param {boolean} props.showModal - Indicates whether the modal is open.
 * @param {function} props.setShowModal - Function to toggle the modal's visibility.
 * @param {function} props.refreshData - Function to refresh the regions data after updating.
 * @returns {JSX.Element} The `UpdateRegionModal` component.
 */
export const UpdateFailureModal = ({
  failure_type_id,
  failureId,
  showModal,
  setShowModal,
  refreshData,
}) => {
  const [isLoading, setIsLoading] = useState(false); // State for tracking loading state
  const [isFetching, setIsFetching] = useState(false); // State for tracking data fetching
  const [failure, setFailure] = useState({
    failure_type_id,
    name: '',
    level: '',
    sla_hours: '',
    description: '',
    notes: '',
  }); // State for new failure data

  const levels = ['1', '2', '3', '4', '5'];
  const hours = ['1', '2', '3', '4', '6', '8', '9', '12', '24', '48', '72', '168'];

  useEffect(() => {
    if (showModal) {
      fetchData(); // Fetch region details when the modal opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  /**
   * Fetches the details of the selected region.
   */
  const fetchData = async () => {
    setIsFetching(true);
    const endpoint = `${GlobalVariables.getURL()}failures/${failureId}`;

    try {
      const response = await customAxios.get(endpoint, HEADERS);
      const failure = response.data.data;
      setFailure({
        ...failure,
        name: failure.name,
        level: failure.level,
        description: failure.description,
        sla_hours: failure.sla_hours,
        notes: failure.notes,
      }); // Update failure state with fetched details
    } catch (error) {
      console.error('Error fetching failure details:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      });
    } finally {
      setIsFetching(false);
    }
  };

  /**
   * Resets the fields in the form to their initial state.
   */
  const handleResetFields = () => {
    setFailure({
      failure_type_id: null,
      name: '',
      level: '',
      sla_hours: '',
      description: '',
      notes: '',
    });
  };

  /**
   * Closes the modal and resets the form fields.
   */
  const handleClose = () => {
    setShowModal(false);
    handleResetFields();
    refreshData(); // Refresh failure types data
  };

  /**
   * Handles the form submission to create a new failure type.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const endpoint = `${GlobalVariables.getURL()}failures/update/${failureId}`;

    try {
      const response = await customAxios.patch(
        endpoint,
        {
          failure_type_id,
          name: failure.name,
          level: failure.level,
          description: failure.description,
          sla_hours: failure.sla_hours,
          notes: failure.notes,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        },
      );

      if (response.status === 200) {
        getToast({
          status: 'success',
          title: 'Falla actualizada exitosamente',
        }); // Show success message
        handleClose(); // Close modal and reset fields
      }
    } catch (error) {
      console.error('Error updating failure:', error);
      getToast({
        status: 'error',
        title: error.response?.data?.message || 'Error',
      }); // Show error message
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  return (
    <Dialog open={showModal} maxWidth='md' fullWidth onClose={handleClose}>
      <DialogTitle>
        <Typography className='inventory-movmodal-title'>
          Editar tipo de falla
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 20,
          top: 20,
          width: 40,
          height: 40,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogContent dividers>
        <form id='new-region-form' onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Name Field */}
            <Grid item xs={12}>
              {isFetching ? (
                <Skeleton variant='rounded' height={56} />
              ) : (
                <TextField
                  required
                  fullWidth
                  disabled={isLoading}
                  label='Nombre falla SN'
                  placeholder='Nombre falla SN...'
                  type='text'
                  value={failure.name}
                  onChange={(e) =>
                    setFailure((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
            <Grid item xs={6}>
              {isFetching ? (
                <Skeleton variant='rounded' height={56} />
              ) : (
                <FormControl fullWidth required>
                  <InputLabel id='select-level-label'>
                    Nivel de la falla
                  </InputLabel>
                  <Select
                    labelId='select-level-label'
                    value={failure.level}
                    label='Nivel de la falla'
                    placeholder='Nivel de la falla...'
                    onChange={(e) =>
                      setFailure((prev) => ({
                        ...prev,
                        level: e.target.value,
                      }))
                    }
                  >
                    {levels.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={6}>
              {isFetching ? (
                <Skeleton variant='rounded' height={56} />
              ) : (
                <FormControl fullWidth required>
                  <InputLabel id='select-hours-label'>Horas</InputLabel>
                  <Select
                    labelId='select-hours-label'
                    value={failure.sla_hours}
                    label='Horas'
                    placeholder='Horas...'
                    onChange={(e) =>
                      setFailure((prev) => ({
                        ...prev,
                        sla_hours: e.target.value,
                      }))
                    }
                  >
                    {hours.map((hours) => (
                      <MenuItem key={hours} value={hours}>
                        {hours}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            {/* Description Field */}
            <Grid item xs={12}>
              {isFetching ? (
                <Skeleton variant='rounded' height={56} />
              ) : (
                <TextField
                  required
                  fullWidth
                  disabled={isLoading}
                  label='Descripción de la falla'
                  placeholder='Descripción de la falla...'
                  multiline
                  minRows={3}
                  maxRows={3}
                  type='text'
                  value={failure.description}
                  onChange={(e) =>
                    setFailure((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
            {/* Notes Field */}
            <Grid item xs={12}>
              {isFetching ? (
                <Skeleton variant='rounded' height={56} />
              ) : (
                <TextField
                  fullWidth
                  disabled={isLoading}
                  label='Notas'
                  placeholder='Notas...'
                  type='text'
                  value={failure.notes}
                  onChange={(e) =>
                    setFailure((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                />
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          variant='contained'
          form='new-region-form'
          type='submit'
          disabled={isLoading}
        >
          {isLoading ? (
            <CircularProgress size='2rem' />
          ) : (
            <Typography>Guardar</Typography>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
